import type { AriaListBoxOptions } from "@react-aria/listbox";
import type { Node } from "@react-types/shared";
import { type RefObject, useRef } from "react";
import { useListBox, useListBoxSection, useOption } from "react-aria";
import type { ListState } from "react-stately";

import { CheckIcon } from "@moment/icons";

import { cn } from "../utils";

interface ListBoxProps extends AriaListBoxOptions<unknown> {
	listBoxRef?: RefObject<HTMLUListElement>;
	state: ListState<unknown>;
	className?: string;
}

interface SectionProps {
	section: Node<unknown>;
	state: ListState<unknown>;
}

interface OptionProps {
	item: Node<unknown>;
	state: ListState<unknown>;
}

export function ListBox(props: ListBoxProps) {
	let ref = useRef<HTMLUListElement>(null);
	let { listBoxRef = ref, state } = props;
	let { listBoxProps } = useListBox(props, state, listBoxRef);

	return (
		<ul
			{...listBoxProps}
			ref={listBoxRef}
			className={cn("w-full max-h-72 overflow-auto outline-none", props.className)}
		>
			{[...state.collection].map((item) =>
				item.type === "section" ? (
					<ListBoxSection key={item.key} section={item} state={state} />
				) : (
					<Option key={item.key} item={item} state={state} />
				)
			)}
		</ul>
	);
}

function ListBoxSection({ section, state }: SectionProps) {
	let { itemProps, headingProps, groupProps } = useListBoxSection({
		"heading": section.rendered,
		"aria-label": section["aria-label"],
	});

	return (
		<>
			<li {...itemProps} className="pt-0">
				{section.rendered && (
					<div
						{...headingProps}
						className="pt-2 pb-1 px-2 text-sm font-semibold text-tertiary"
					>
						{section.rendered}
					</div>
				)}
				<ul {...groupProps}>
					{[...section.childNodes].map((node) => (
						<Option key={node.key} item={node} state={state} />
					))}
				</ul>
			</li>
		</>
	);
}

function Option({ item, state }: OptionProps) {
	let ref = useRef<HTMLLIElement>(null);
	let { optionProps, isDisabled, isSelected, isFocused } = useOption(
		{
			key: item.key,
		},
		state,
		ref
	);

	return (
		<li
			{...optionProps}
			ref={ref}
			className={cn(
				"relative",
				"flex",
				"w-full",
				"cursor-default",
				"select-none",
				"items-center",
				"rounded",
				"py-1.5",
				"px-2",
				"text-sm",
				"outline-none",
				"[&_svg]:shrink-0",
				{
					"[&_svg]:text-palette-gray-11": !isFocused,
					"[&_svg]:text-fixed-light-default": isFocused,
					"[&_.text-secondary]:text-fixed-light-default": isFocused,

					"bg-palette-brand-9": isFocused,
					"text-fixed-light-default": isFocused,

					"pointer-events-none": isDisabled,
					"opacity-50": isDisabled,
					"cursor-not-allowed": isDisabled,
				}
				// className
			)}
		>
			{item.rendered}
			{isSelected && <CheckIcon aria-hidden="true" className="ml-auto" />}
		</li>
	);
}
